import type { Lane } from "../data/model";
import type { RunMetrics } from "../engine/RunSession";

/**
 * Missions: sets of three; a finished set raises the permanent multiplier by one (x1 → x30), later sets pay a Super
 * Mystery Box. The ladder mirrors the original's shape (coins → score → moves → power-ups → stumbles → long goals)
 * with our road: ETH trucks, barriers, ELON (the chaser), SOL.
 */
export type MissionTarget =
  | "collectCoins" | "scorePoints" | "jump" | "roll" | "dodgeBarriers" | "rideTrucks" | "pickupMagnets" | "pickupJetpacks"
  | "pickupSneakers" | "pickupPogo" | "pickupPowerups" | "useBoards" | "collectKeys" | "pickupBoxes" | "stumbleBarriers"
  | "stumblePoles" | "stumbleBushes" | "stumbleTrucks" | "escapeCop" | "getCaughtFast" | "beatHighScore" | "spendCoins"
  | "buyBoxes" | "completeWordHunts" | "coinsWithMagnet" | "coinsWithJetpack" | "coinsInAir" | "coinsWithSneakers"
  | "stayInLane" | "noCoinsScore" | "rollMiddleLane" | "distance" | "hopEth" | "useHeadstart" | "useBoosters" | "revive";

export interface MissionDefinition {
  target: MissionTarget;
  amount: number;
  /** must happen inside one run (otherwise progress adds up across runs) */
  singleRun?: boolean;
  lane?: Lane;
}

export interface MissionState {
  setIndex: number;
  progress: [number, number, number];
  done: [boolean, boolean, boolean];
}

/** What the shop and the hunts report so missions like "spend 500 SOL" can count. */
export interface MetaEvents {
  spentCoins: number;
  boughtBoxes: number;
  wordHuntsCompleted: number;
  highScoreBeaten: boolean;
}

const m = (target: MissionTarget, amount: number, extra: Partial<MissionDefinition> = {}): MissionDefinition => ({ target, amount, ...extra });

export const missionSets: MissionDefinition[][] = [
  [m("collectCoins", 100), m("scorePoints", 500, { singleRun: true }), m("jump", 10)],
  [m("collectCoins", 100, { singleRun: true }), m("jump", 15), m("dodgeBarriers", 10)],
  [m("scorePoints", 5000), m("roll", 15), m("spendCoins", 500)],
  [m("collectCoins", 200, { singleRun: true }), m("pickupMagnets", 3), m("scorePoints", 3000, { singleRun: true })],
  [m("collectCoins", 2500), m("jump", 30, { singleRun: true }), m("buyBoxes", 1)],
  [m("useBoards", 1), m("pickupMagnets", 5), m("beatHighScore", 1)],
  [m("pickupJetpacks", 2), m("coinsWithJetpack", 50), m("rideTrucks", 5)],
  [m("stumbleTrucks", 3, { singleRun: true }), m("coinsWithMagnet", 40), m("getCaughtFast", 1)],
  [m("useBoards", 2), m("pickupBoxes", 2), m("roll", 30, { singleRun: true })],
  [m("scorePoints", 10000, { singleRun: true }), m("pickupPowerups", 12), m("collectCoins", 2000)],
  [m("noCoinsScore", 2000), m("rollMiddleLane", 50), m("collectCoins", 5000)],
  [m("completeWordHunts", 2), m("dodgeBarriers", 40), m("pickupSneakers", 5)],
  [m("escapeCop", 4), m("coinsWithMagnet", 160), m("pickupMagnets", 2, { singleRun: true })],
  [m("pickupPogo", 4), m("roll", 40, { singleRun: true }), m("collectCoins", 400, { singleRun: true })],
  [m("scorePoints", 50000), m("pickupJetpacks", 5), m("stumblePoles", 12)],
  [m("completeWordHunts", 3), m("pickupSneakers", 3, { singleRun: true }), m("collectCoins", 5000)],
  [m("scorePoints", 25000, { singleRun: true }), m("spendCoins", 4000), m("pickupMagnets", 15)],
  [m("pickupJetpacks", 2, { singleRun: true }), m("stumbleTrucks", 6, { singleRun: true }), m("useBoards", 3)],
  [m("collectKeys", 3), m("pickupMagnets", 3, { singleRun: true }), m("collectCoins", 8000)],
  [m("scorePoints", 120000), m("jump", 40, { singleRun: true }), m("pickupPogo", 4, { singleRun: true })],
  [m("stumbleBarriers", 15), m("buyBoxes", 3), m("coinsWithMagnet", 240)],
  [m("dodgeBarriers", 80), m("spendCoins", 5000), m("pickupSneakers", 4, { singleRun: true })],
  [m("coinsInAir", 50), m("collectCoins", 10000), m("pickupJetpacks", 15)],
  [m("coinsWithSneakers", 500), m("rollMiddleLane", 200), m("completeWordHunts", 4)],
  [m("collectCoins", 15000), m("scorePoints", 60000, { singleRun: true }), m("pickupPowerups", 15, { lane: -1 })],
  [m("roll", 50, { singleRun: true }), m("collectCoins", 30000), m("noCoinsScore", 6000)],
  [m("stumbleBarriers", 6, { singleRun: true }), m("buyBoxes", 6), m("stumblePoles", 20)],
  [m("jump", 50, { singleRun: true }), m("useBoards", 12), m("useBoards", 3, { singleRun: true })],
  [m("collectCoins", 500, { singleRun: true }), m("stumbleBarriers", 25), m("scorePoints", 125000, { singleRun: true })],
  [m("hopEth", 30), m("rideTrucks", 25), m("escapeCop", 10)]
];

export const MAX_MULTIPLIER_SETS = 30;

/** Sets past the ladder: scaled repeats; each pays a Super Mystery Box instead of a multiplier. */
export function missionSet(index: number): MissionDefinition[] {
  if (index < missionSets.length) return missionSets[index];
  const base = missionSets[index % missionSets.length];
  const scale = 1 + Math.floor(index / missionSets.length) * 0.5;
  return base.map((d) => ({ ...d, amount: d.target === "beatHighScore" || d.target === "getCaughtFast" ? d.amount : Math.round(d.amount * scale) }));
}

const NOUN: Record<MissionTarget, (n: number) => string> = {
  collectCoins: (n) => `Collect ${n} SOL`, scorePoints: (n) => `Score ${n.toLocaleString("en-US")} points`, jump: (n) => `Hop ${n} times`,
  roll: (n) => `Duck ${n} times`, dodgeBarriers: (n) => `Dodge ${n} barriers`, rideTrucks: (n) => `Ride on top of ${n} ETH trucks`,
  pickupMagnets: (n) => `Pick up ${n} magnets`, pickupJetpacks: (n) => `Pick up ${n} thrusters`, pickupSneakers: (n) => `Pick up ${n} super springs`,
  pickupPogo: (n) => `Pick up ${n} mega hops`, pickupPowerups: (n) => `Pick up ${n} power-ups`, useBoards: (n) => `Use ${n} hover boards`,
  collectKeys: (n) => `Pick up ${n} keys`, pickupBoxes: (n) => `Pick up ${n} mystery boxes`, stumbleBarriers: (n) => `Stumble into ${n} barriers`,
  stumblePoles: (n) => `Stumble into ${n} traffic lights`, stumbleBushes: (n) => `Stumble into ${n} hedges`, stumbleTrucks: (n) => `Stumble into ${n} ETH trucks`,
  escapeCop: (n) => `Escape Elon ${n} times`, getCaughtFast: () => `Get caught in the first 10 seconds`, beatHighScore: (n) => `Beat your high score ${n} time${n > 1 ? "s" : ""}`,
  spendCoins: (n) => `Spend ${n} SOL`, buyBoxes: (n) => `Buy ${n} mystery box${n > 1 ? "es" : ""}`, completeWordHunts: (n) => `Complete ${n} Word Hunt${n > 1 ? "s" : ""}`,
  coinsWithMagnet: (n) => `Pick up ${n} SOL with a magnet`, coinsWithJetpack: (n) => `Pick up ${n} SOL with thrusters`, coinsInAir: (n) => `Pick up ${n} SOL in the air`,
  coinsWithSneakers: (n) => `Pick up ${n} SOL with super springs`, stayInLane: (n) => `Stay in one lane for ${n} seconds`, noCoinsScore: (n) => `Score ${n} points without picking up SOL`,
  rollMiddleLane: (n) => `Duck ${n} times in the middle lane`, distance: (n) => `Drive ${n} m`, hopEth: (n) => `Hop ${n} ETH coins`,
  useHeadstart: (n) => `Use ${n} headstart${n > 1 ? "s" : ""}`, useBoosters: (n) => `Use ${n} score boosters`, revive: (n) => `Continue ${n} time${n > 1 ? "s" : ""}`
};

export function missionText(def: MissionDefinition): string {
  let text = NOUN[def.target](def.amount);
  if (def.lane !== undefined) text += def.lane === -1 ? " in the left lane" : def.lane === 1 ? " in the right lane" : " in the middle lane";
  if (def.singleRun) text += " in one run";
  return text;
}

/** The run's own count for a mission (single-run missions compare this to the amount; the rest add it up). */
export function runValue(def: MissionDefinition, run: RunMetrics, meta: MetaEvents): number {
  switch (def.target) {
    case "collectCoins": return run.coins;
    case "scorePoints": return run.score;
    case "jump": return run.jumps;
    case "roll": return run.rolls;
    case "dodgeBarriers": return run.dodges;
    case "rideTrucks": return run.rides;
    case "pickupMagnets": return run.pickups.magnet ?? 0;
    case "pickupJetpacks": return run.pickups.jetpack ?? 0;
    case "pickupSneakers": return run.pickups.sneakers ?? 0;
    case "pickupPogo": return run.pickups.pogo ?? 0;
    case "pickupPowerups": return def.lane !== undefined ? run.pickupsByLane[String(def.lane)] ?? 0 : Object.values(run.pickups).reduce<number>((a, b) => a + (b ?? 0), 0);
    case "useBoards": return run.boardsUsed;
    case "collectKeys": return run.keys;
    case "pickupBoxes": return run.boxes;
    case "stumbleBarriers": return (run.stumblesByKind.barrierLow ?? 0) + (run.stumblesByKind.barrierBar ?? 0) + (run.stumblesByKind.barrierHigh ?? 0);
    case "stumblePoles": return run.stumblesByKind.pole ?? 0;
    case "stumbleBushes": return run.stumblesByKind.bush ?? 0;
    case "stumbleTrucks": return (run.stumblesByKind.truck ?? 0) + (run.stumblesByKind.ramp ?? 0);
    case "escapeCop": return run.escapes;
    case "getCaughtFast": return run.caughtAtSeconds !== null && run.caughtAtSeconds <= 10 ? 1 : 0;
    case "beatHighScore": return meta.highScoreBeaten ? 1 : 0;
    case "spendCoins": return meta.spentCoins;
    case "buyBoxes": return meta.boughtBoxes;
    case "completeWordHunts": return meta.wordHuntsCompleted;
    case "coinsWithMagnet": return run.coinsWithMagnet;
    case "coinsWithJetpack": return run.coinsWithJetpack;
    case "coinsInAir": return run.coinsInAir;
    case "coinsWithSneakers": return run.coinsWithSneakers;
    case "stayInLane": return run.longestLaneSeconds;
    case "noCoinsScore": return run.coins === 0 ? run.score : 0;
    case "rollMiddleLane": return run.rollsMiddle;
    case "distance": return run.distance;
    case "hopEth": return run.ethHopped;
    case "useHeadstart": return run.headstarts;
    case "useBoosters": return run.boosters;
    case "revive": return run.revives;
  }
}

export interface MissionResult {
  completed: MissionDefinition[];
  setCompleted: boolean;
  superBox: boolean;
}

/** Applies a finished run (and anything the shop/hunts reported) to the mission state. */
export function applyRun(state: MissionState, run: RunMetrics, meta: MetaEvents): MissionResult {
  const defs = missionSet(state.setIndex);
  const completed: MissionDefinition[] = [];
  defs.forEach((def, i) => {
    if (state.done[i]) return;
    const value = runValue(def, run, meta);
    const singleLike = def.singleRun || def.target === "beatHighScore" || def.target === "getCaughtFast" || def.target === "noCoinsScore" || def.target === "stayInLane";
    state.progress[i] = singleLike ? Math.max(state.progress[i], value) : state.progress[i] + value;
    if (state.progress[i] >= def.amount) { state.done[i] = true; completed.push(def); }
  });
  const setCompleted = state.done.every(Boolean);
  const superBox = setCompleted && state.setIndex >= MAX_MULTIPLIER_SETS;
  if (setCompleted) {
    state.setIndex += 1;
    state.progress = [0, 0, 0];
    state.done = [false, false, false];
  }
  return { completed, setCompleted, superBox };
}

/** Skip one mission for SOL: the price grows with the set. ⚠ ours */
export function skipCost(setIndex: number): number {
  return 500 + setIndex * 250;
}

export function skipMission(state: MissionState, index: number): boolean {
  if (state.done[index]) return false;
  state.done[index] = true;
  state.progress[index] = missionSet(state.setIndex)[index].amount;
  if (state.done.every(Boolean)) {
    state.setIndex += 1;
    state.progress = [0, 0, 0];
    state.done = [false, false, false];
  }
  return true;
}

/** Live progress for the HUD/pause: stored progress plus the current run's part. */
export function liveProgress(state: MissionState, run: RunMetrics, meta: MetaEvents): { def: MissionDefinition; value: number; done: boolean }[] {
  return missionSet(state.setIndex).map((def, i) => {
    const value = runValue(def, run, meta);
    const singleLike = def.singleRun || def.target === "beatHighScore" || def.target === "getCaughtFast" || def.target === "noCoinsScore" || def.target === "stayInLane";
    const total = state.done[i] ? def.amount : singleLike ? Math.max(state.progress[i], value) : state.progress[i] + value;
    return { def, value: Math.min(def.amount, total), done: state.done[i] || total >= def.amount };
  });
}
